// School admin: the student-system settings and every class in the school, with Create
// Class, plus the school-wide admin tabs (reports and export, content approval, consent
// status, the safety queue, year-end rollover). A super admin picks the school first.
// Rosters, a class's own report and sharing live on the class detail page.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { createLogger } from '../../utils/logger';
import LoadingIndicator from '../loading-indicator/LoadingIndicator';
import { T } from '../gamification/shared';
import OrgSelect from '../common/OrgSelect';
import {
  ClassInput,
  ClassList,
  ClassStatus,
  StudentSystemSettings,
  createClass,
  fetchClasses,
} from '../../services/classService';
import ClassFormModal from './ClassFormModal';
import ConsentStatusTab from './ConsentStatusTab';
import ContentApprovalTab from './ContentApprovalTab';
import RolloverTab from './RolloverTab';
import SafetyQueueTab from './SafetyQueueTab';
import SchoolReportTab from './SchoolReportTab';
import StudentSystemSettingsCard from './StudentSystemSettingsCard';
import { recordStudentSystemEnabled } from './useClassNav';
import {
  cardStyle,
  errorStyle,
  headerRowStyle,
  hintStyle,
  inputStyle,
  labelStyle,
  pageStyle,
  primaryButtonStyle,
  secondaryButtonStyle,
  statusBadgeStyle,
  subtitleStyle,
  tableStyle,
  tdStyle,
  thStyle,
  titleStyle,
} from './classesStyles';

const logger = createLogger('ClassesPage');
const PAGE_SIZE = 25;

const TABS = ['classes', 'reports', 'content', 'consent', 'safety', 'rollover'] as const;
type Tab = (typeof TABS)[number];

const tabStyle = (active: boolean): React.CSSProperties => ({
  background: 'none',
  border: 'none',
  borderBottom: `2px solid ${active ? T.primary : 'transparent'}`,
  color: active ? T.primary : T.muted,
  fontWeight: 600,
  fontSize: '0.875rem',
  padding: '0.5rem 0.25rem',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

const ClassesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const isSuperAdmin = userRole === 'super_admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') as Tab | null;
  const [tab, setTab] = useState<Tab>(requestedTab && TABS.includes(requestedTab) ? requestedTab : 'classes');

  // An org admin's own org is implied server-side; a super admin works on the one picked here.
  const [orgId, setOrgId] = useState<string | null>(isSuperAdmin ? null : currentUser?.organization_id ?? null);
  const [settings, setSettings] = useState<StudentSystemSettings | null>(null);
  const [list, setList] = useState<ClassList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ClassStatus | ''>('active');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (isSuperAdmin && !orgId) return;
    setLoading(true);
    setError(null);
    try {
      setList(await fetchClasses({ page, pageSize: PAGE_SIZE, search: search.trim(), status, organizationId: orgId }));
    } catch (err) {
      logger.error('Failed to load classes', err);
      setError(t('classes.couldNotLoadClasses'));
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, orgId, page, search, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onSettings = useCallback(
    (next: StudentSystemSettings) => {
      setSettings(next);
      if (!isSuperAdmin) {
        recordStudentSystemEnabled(currentUser?.id, userRole, currentUser?.organization_id, next.student_system_enabled);
      }
    },
    [isSuperAdmin, currentUser?.id, currentUser?.organization_id, userRole]
  );

  const create = async (input: ClassInput) => {
    const created = await createClass({ ...input, ...(isSuperAdmin && orgId ? { organization_id: orgId } : {}) });
    setCreating(false);
    navigate(`/classes/${created.id}`);
  };

  const enabled = settings?.student_system_enabled ?? false;
  const totalPages = list?.pagination.total_pages ?? 1;

  const openTab = (next: Tab) => {
    setTab(next);
    setSearchParams(next === 'classes' ? {} : { tab: next }, { replace: true });
  };

  return (
    <div style={pageStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 id="tour-page-title" style={titleStyle}>
            {t('sidebar.classes')}
          </h1>
          <p style={subtitleStyle}>{t('classes.pageSubtitle')}</p>
        </div>
        {enabled && tab === 'classes' && (
          <button id="tour-create-class" type="button" style={primaryButtonStyle} onClick={() => setCreating(true)}>
            <Plus size={16} /> {t('classes.createClass')}
          </button>
        )}
      </div>

      {isSuperAdmin && (
        <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <label htmlFor="classes-org" style={{ ...labelStyle, margin: 0 }}>
            {t('classes.organization')}
          </label>
          {/* Searched on the server as the admin types: there are thousands of schools, and a
              plain <select> of the first page cut off every name past it (Boys Town included). */}
          <div style={{ minWidth: 320, flex: '0 1 420px' }}>
            <OrgSelect
              inputId="classes-org"
              value={orgId}
              allowPlatformWide={false}
              onChange={id => {
                setOrgId(id);
                setSettings(null);
                setList(null);
                setPage(1);
              }}
            />
          </div>
        </div>
      )}

      {(!isSuperAdmin || orgId) && (
        <StudentSystemSettingsCard key={orgId ?? 'own'} organizationId={orgId} onChange={onSettings} />
      )}

      {settings && !enabled && <p style={{ ...hintStyle, marginBottom: '1rem' }}>{t('classes.turnOnToCreate')}</p>}

      {enabled && (
        <div
          id="tour-page-primary-action"
          role="tablist"
          aria-label={t('studentAdmin.tabsLabel')}
          style={{ display: 'flex', gap: 20, borderBottom: `1px solid ${T.border}`, marginBottom: '1.25rem', overflowX: 'auto' }}
        >
          {TABS.map(name => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={tab === name}
              style={tabStyle(tab === name)}
              onClick={() => openTab(name)}
            >
              {t(`studentAdmin.tabs.${name}`)}
            </button>
          ))}
        </div>
      )}

      {enabled && tab !== 'classes' && (
        <div id="tour-page-content" role="tabpanel">
          {tab === 'reports' && <SchoolReportTab key={orgId ?? 'own'} organizationId={orgId} />}
          {tab === 'content' && (
            <ContentApprovalTab
              key={orgId ?? 'own'}
              organizationId={orgId}
              approvalRequired={settings?.student_content_approval_required ?? true}
            />
          )}
          {tab === 'consent' && <ConsentStatusTab key={orgId ?? 'own'} organizationId={orgId} />}
          {tab === 'safety' && <SafetyQueueTab key={orgId ?? 'own'} organizationId={orgId} />}
          {tab === 'rollover' && <RolloverTab key={orgId ?? 'own'} organizationId={orgId} />}
        </div>
      )}

      {error && tab === 'classes' && <div style={errorStyle} role="alert">{error}</div>}

      {list && (!enabled || tab === 'classes') && (
        <section id="tour-page-content" style={cardStyle}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: '1rem' }}>
            <input
              type="search"
              aria-label={t('classes.searchClasses')}
              placeholder={t('classes.searchClasses')}
              style={{ ...inputStyle, flex: '1 1 220px' }}
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <select
              aria-label={t('common.status')}
              style={inputStyle}
              value={status}
              onChange={e => {
                setStatus(e.target.value as ClassStatus | '');
                setPage(1);
              }}
            >
              <option value="active">{t('classes.statuses.active')}</option>
              <option value="archived">{t('classes.statuses.archived')}</option>
              <option value="">{t('common.all')}</option>
            </select>
          </div>

          {loading ? (
            <LoadingIndicator message={t('classes.loading')} />
          ) : list.items.length === 0 ? (
            <p style={hintStyle}>{t('classes.noClasses')}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>{t('common.name')}</th>
                    <th style={thStyle}>{t('classes.fields.subject')}</th>
                    <th style={thStyle}>{t('classes.fields.grade_range')}</th>
                    <th style={thStyle}>{t('classes.fields.school_year')}</th>
                    <th style={thStyle}>{t('classes.teachers')}</th>
                    <th style={thStyle}>{t('classes.students')}</th>
                    <th style={thStyle}>{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {list.items.map(c => (
                    <tr key={c.id}>
                      <td style={tdStyle}>
                        <Link to={`/classes/${c.id}`} style={{ fontWeight: 600 }}>
                          {c.name}
                        </Link>
                      </td>
                      <td style={tdStyle}>{c.subject || '—'}</td>
                      <td style={tdStyle}>{c.grade_range || '—'}</td>
                      <td style={tdStyle}>{c.school_year || '—'}</td>
                      <td style={tdStyle}>{c.teacher_count}</td>
                      <td style={tdStyle}>{c.student_count}</td>
                      <td style={tdStyle}>
                        <span style={statusBadgeStyle(c.status === 'active')}>{t(`classes.statuses.${c.status}`)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: '1rem' }}>
              <button type="button" style={secondaryButtonStyle} disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                {t('classes.previousPage')}
              </button>
              <span style={{ fontSize: '0.85rem' }}>{t('classes.pageOf', { page, total: totalPages })}</span>
              <button
                type="button"
                style={secondaryButtonStyle}
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                {t('classes.nextPage')}
              </button>
            </div>
          )}
        </section>
      )}

      {creating && <ClassFormModal onSubmit={create} onClose={() => setCreating(false)} />}
    </div>
  );
};

export default ClassesPage;
